import React from "react";
import { Card, Checkbox, List, Table, Typography } from "antd";
import TableActions from "components/table-actions";

const { Text } = Typography;

const ACTION_DATA_INDEXS = ["action", "actions"];

function isActionColumn(col) {
  return (
    ACTION_DATA_INDEXS.includes(col.dataIndex) ||
    (col.title && String(col.title).toLowerCase() === "ações")
  );
}

function getDataColumns(columns) {
  return (columns || []).filter((col) => !isActionColumn(col));
}

function findColumn(columns, dataIndex) {
  return (columns || []).find((col) => col.dataIndex === dataIndex);
}

function renderCellValue(col, record) {
  if (!col) {
    return null;
  }
  const value = record[col.dataIndex];
  if (col.render) {
    return col.render(value, record, 0);
  }
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return value;
}

function getRecordKey(record, rowKey) {
  if (typeof rowKey === "function") {
    return rowKey(record);
  }
  return record[rowKey];
}

function extractActionsFromColumn(actionColumn, record) {
  if (!actionColumn || !actionColumn.render) {
    return [];
  }

  const rendered = actionColumn.render(null, record, 0);
  if (!rendered) {
    return [];
  }

  if (Array.isArray(rendered)) {
    return rendered.map((node, index) => ({
      key: String(index),
      label: node?.props?.title || `Ação ${index + 1}`,
      icon: node,
      node,
    }));
  }

  if (rendered?.props?.children) {
    const children = React.Children.toArray(rendered.props.children);
    return children.map((child, index) => ({
      key: String(index),
      label: child?.props?.title || `Ação ${index + 1}`,
      icon: child,
      node: child,
    }));
  }

  return [
    {
      key: "0",
      label: rendered?.props?.title || "Ação",
      icon: rendered,
      node: rendered,
    },
  ];
}

function HistoryCardItem({
  record,
  dataColumns,
  actionColumn,
  rowClassName,
}) {
  const actions = extractActionsFromColumn(actionColumn, record);
  const dateCol =
    findColumn(dataColumns, "reviewDate") ||
    findColumn(dataColumns, "sprintDate") ||
    dataColumns.find((col) => String(col.title).toLowerCase() === "data") ||
    dataColumns[0];
  const metaColumns = dataColumns.filter((col) => col !== dateCol);
  const rowClass =
    typeof rowClassName === "function"
      ? rowClassName(record)
      : rowClassName || "";

  return (
    <Card
      size="small"
      style={{ width: "100%" }}
      className={`responsive-card-item history-card ${rowClass}`.trim()}
    >
      {dateCol && (
        <div className="card-history-date">{renderCellValue(dateCol, record)}</div>
      )}
      {metaColumns.map((col) => (
        <div key={col.title || col.dataIndex} className="card-history-meta">
          <span className="card-history-meta-label">{col.title}:</span>{" "}
          {renderCellValue(col, record)}
        </div>
      ))}
      {actions.length > 0 && (
        <div className="card-profile-actions">
          <TableActions actions={actions} isMobile inline />
        </div>
      )}
    </Card>
  );
}

function ProfileCardItem({
  record,
  columns,
  actionColumn,
  rowKey,
  rowSelection,
  rowClassName,
}) {
  const actions = extractActionsFromColumn(actionColumn, record);
  const imageCol = findColumn(columns, "image");
  const nameCol = findColumn(columns, "name");
  const emailCol = findColumn(columns, "email");
  const profileCol = findColumn(columns, "profile");
  const birthDateCol = findColumn(columns, "birthDate");
  const methodCol = findColumn(columns, "method");
  const dateCol =
    findColumn(columns, "reviewDate") ||
    findColumn(columns, "lastPlanning");
  const recordKey = getRecordKey(record, rowKey);
  const isSelected = rowSelection?.selectedRowKeys?.includes(recordKey);

  const handleSelectChange = (checked) => {
    if (!rowSelection?.onChange) {
      return;
    }
    const currentKeys = rowSelection.selectedRowKeys || [];
    const nextKeys = checked
      ? [...currentKeys, recordKey]
      : currentKeys.filter((key) => key !== recordKey);
    rowSelection.onChange(
      nextKeys,
      checked ? [record] : [],
      { type: "single" }
    );
  };

  const dateValue = dateCol ? renderCellValue(dateCol, record) : null;
  const hasDate = dateValue && dateValue !== "—" && dateValue !== "";
  const emptyDateLabel =
    dateCol?.dataIndex === "lastPlanning"
      ? "Sem planificação"
      : "Sem avaliação";

  const rowClass =
    typeof rowClassName === "function"
      ? rowClassName(record)
      : rowClassName || "";

  return (
    <Card
      size="small"
      style={{ width: "100%" }}
      className={`responsive-card-item profile-card ${rowClass}`.trim()}
    >
      <div className="card-profile-header">
        {rowSelection && (
          <Checkbox
            checked={isSelected}
            onChange={(event) => handleSelectChange(event.target.checked)}
            className="card-profile-checkbox"
          />
        )}
        {imageCol && (
          <div className="card-profile-avatar">
            {renderCellValue(imageCol, record)}
          </div>
        )}
        <div className="card-profile-info">
          {nameCol && (
            <div className="card-profile-name">
              {renderCellValue(nameCol, record)}
            </div>
          )}
          {emailCol && (
            <div className="card-profile-email">
              {renderCellValue(emailCol, record)}
            </div>
          )}
          {(profileCol || methodCol) && (
            <div className="card-profile-tags">
              {profileCol && renderCellValue(profileCol, record)}
              {methodCol && renderCellValue(methodCol, record)}
            </div>
          )}
          {birthDateCol && (
            <div className="card-profile-meta">
              {birthDateCol.title}: {renderCellValue(birthDateCol, record) || "—"}
            </div>
          )}
          {dateCol && (
            <div className="card-profile-meta">
              {dateCol.title}: {hasDate ? dateValue : emptyDateLabel}
            </div>
          )}
        </div>
      </div>

      {actions.length > 0 && (
        <div className="card-profile-actions">
          <TableActions actions={actions} isMobile inline />
        </div>
      )}
    </Card>
  );
}

function RankingCardItem({
  record,
  columns,
  rowClassName,
}) {
  const rankCol = findColumn(columns, "rank");
  const imgCol = findColumn(columns, "img");
  const nameCol = findColumn(columns, "name");
  const ageCol = findColumn(columns, "age");
  const metaColumns = columns.filter(
    (col) =>
      col !== rankCol &&
      col !== imgCol &&
      col !== nameCol &&
      col !== ageCol
  );
  const rowClass =
    typeof rowClassName === "function"
      ? rowClassName(record)
      : rowClassName || "";

  return (
    <Card
      size="small"
      style={{ width: "100%" }}
      className={`responsive-card-item profile-card ranking-card ${rowClass}`.trim()}
    >
      <div className="card-profile-header">
        <div className="card-profile-avatar card-metric-badge card-ranking-badge">
          <span>
            {rankCol ? renderCellValue(rankCol, record) : `${record.rank}º`}
          </span>
        </div>
        {imgCol && (
          <div className="card-profile-avatar">
            {renderCellValue(imgCol, record)}
          </div>
        )}
        <div className="card-profile-info">
          {nameCol && (
            <div className="card-profile-name">
              {renderCellValue(nameCol, record)}
            </div>
          )}
          {ageCol && (
            <div className="card-profile-meta">
              {ageCol.title}: {renderCellValue(ageCol, record)}
            </div>
          )}
          {metaColumns.map((col) => (
            <div key={col.dataIndex || col.title} className="card-profile-meta">
              {col.title}: {renderCellValue(col, record)}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function MetricCardItem({
  record,
  columns,
  actionColumn,
  rowClassName,
}) {
  const actions = extractActionsFromColumn(actionColumn, record);
  const loadCol = findColumn(columns, "load");
  const trainingCol = findColumn(columns, "orderTraining");
  const vmpCols = ["vmp1", "vmp2", "vmp3", "vmp4"]
    .map((key) => findColumn(columns, key))
    .filter(Boolean);
  const rowClass =
    typeof rowClassName === "function"
      ? rowClassName(record)
      : rowClassName || "";

  return (
    <Card
      size="small"
      style={{ width: "100%" }}
      className={`responsive-card-item profile-card metric-card ${rowClass}`.trim()}
    >
      <div className="card-profile-header">
        <div className="card-profile-avatar card-metric-badge">
          <span>{record.load}</span>
        </div>
        <div className="card-profile-info">
          <div className="card-profile-name">
            <span>Semana </span>
            {loadCol ? renderCellValue(loadCol, record) : record.load}
          </div>
          {trainingCol && (
            <div className="card-profile-email card-metric-training">
              {renderCellValue(trainingCol, record)}
            </div>
          )}
          {vmpCols.length > 0 && (
            <div className="card-metric-grid">
              {vmpCols.map((col) => (
                <div key={col.dataIndex} className="card-metric-item">
                  <span className="card-metric-label">{col.title}</span>
                  <span className="card-metric-value">
                    {renderCellValue(col, record)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {actions.length > 0 && (
        <div className="card-profile-actions">
          <TableActions actions={actions} isMobile inline />
        </div>
      )}
    </Card>
  );
}

function DefaultCardItem({
  record,
  dataColumns,
  actionColumn,
  rowClassName,
  isMobile,
}) {
  const actions = extractActionsFromColumn(actionColumn, record);
  const rowClass =
    typeof rowClassName === "function"
      ? rowClassName(record)
      : rowClassName || "";

  return (
    <Card
      size="small"
      style={{ width: "100%" }}
      className={`responsive-card-item ${rowClass}`.trim()}
    >
      {dataColumns.map((col) => (
        <div key={col.dataIndex || col.title} className="card-field">
          <Text type="secondary" className="card-field-label">
            {col.title}
          </Text>
          <span className="card-field-value">
            {renderCellValue(col, record)}
          </span>
        </div>
      ))}

      {actions.length > 0 && (
        <div className="card-actions">
          <TableActions actions={actions} isMobile={isMobile} />
        </div>
      )}
    </Card>
  );
}

const ResponsiveDataList = ({
  columns,
  dataSource,
  isMobile,
  forceCardLayout,
  cardVariant,
  pagination,
  locale,
  scroll,
  size,
  rowKey,
  rowClassName,
  rowSelection,
  ...tableProps
}) => {
  const dataColumns = getDataColumns(columns);
  const actionColumn = (columns || []).find(isActionColumn);
  const useCardLayout = isMobile || forceCardLayout;

  if (useCardLayout) {
    const cardPagination = pagination
      ? {
          ...pagination,
          ...(isMobile
            ? { simple: true, pageSize: pagination.pageSize || 5 }
            : {}),
          showSizeChanger: false,
        }
      : isMobile
      ? { simple: true, pageSize: 5, showSizeChanger: false }
      : { pageSize: 10, showSizeChanger: false };

    return (
      <List
        className={
          cardVariant === "profile" ||
          cardVariant === "metric" ||
          cardVariant === "ranking"
            ? "responsive-profile-list"
            : cardVariant === "history"
            ? "responsive-history-list"
            : undefined
        }
        dataSource={dataSource}
        locale={locale}
        pagination={cardPagination}
        renderItem={(record) => (
          <List.Item style={{ padding: "8px 0", border: "none" }}>
            {cardVariant === "profile" ? (
              <ProfileCardItem
                record={record}
                columns={dataColumns}
                actionColumn={actionColumn}
                rowKey={rowKey}
                rowSelection={rowSelection}
                rowClassName={rowClassName}
              />
            ) : cardVariant === "metric" ? (
              <MetricCardItem
                record={record}
                columns={dataColumns}
                actionColumn={actionColumn}
                rowClassName={rowClassName}
              />
            ) : cardVariant === "ranking" ? (
              <RankingCardItem
                record={record}
                columns={dataColumns}
                rowClassName={rowClassName}
              />
            ) : cardVariant === "history" ? (
              <HistoryCardItem
                record={record}
                dataColumns={dataColumns}
                actionColumn={actionColumn}
                rowClassName={rowClassName}
              />
            ) : (
              <DefaultCardItem
                record={record}
                dataColumns={dataColumns}
                actionColumn={actionColumn}
                rowClassName={rowClassName}
                isMobile={isMobile}
              />
            )}
          </List.Item>
        )}
      />
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={pagination}
      locale={locale}
      scroll={scroll || { x: "max-content" }}
      size={size}
      rowKey={rowKey}
      rowClassName={rowClassName}
      rowSelection={rowSelection}
      {...tableProps}
    />
  );
};

export default ResponsiveDataList;
